import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { kebabCase } from 'lodash';
import { DataGrid, gridClasses, } from '@mui/x-data-grid';
import CheckIcon from '@mui/icons-material/Check';
import { Link, styled, useTheme } from '@mui/material';
const formatValue = ({ value, isDate = false, trueIcon, falseIcon, }) => {
    let formattedValue;
    if (typeof value === 'boolean') {
        formattedValue = value ?
            (trueIcon ?? _jsx(CheckIcon, {})) :
            (falseIcon ?? '');
    }
    else if (typeof value === 'number') {
        formattedValue = value.toString();
    }
    else if (typeof value === 'string' && isDate) {
        try {
            const date = new Date(value);
            if (date.toString() === 'Invalid Date') {
                throw new Error('Invalid Date');
            }
            formattedValue = date.toLocaleDateString();
        }
        catch {
            formattedValue = value;
        }
    }
    else {
        formattedValue = value ?? '';
    }
    return formattedValue;
};
const StyledDataGrid = styled(DataGrid)(props => ({
    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
        outline: 'none !important',
    },
    '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus': {
        outline: 'none !important',
    },
    '& .MuiDataGrid-row': {
        cursor: props.onRowClick ? 'pointer' : 'undefined',
    },
}));
const Table = ({ headers, rows, searchText, sortBy, sortOrder = 'asc', trueIcon, falseIcon, onSortChange, sortingMode = 'client', sortingOrder = ['asc', 'desc', null], onRowClick, isStickyHeader = false, stickyColumnTop = '242px', rowHeight = undefined, }) => {
    const theme = useTheme();
    const dataColumns = headers.map(({ key, label, width, flex, isSortable, align, type = 'singleSelect', valueGetter = undefined, ...rest }) => {
        const widthData = { width: undefined, flex: undefined };
        if (width) {
            widthData.width = width;
        }
        else if (flex) {
            widthData.flex = flex;
        }
        else {
            widthData.flex = 1;
        }
        return {
            ...widthData,
            field: key,
            headerName: label,
            sortable: isSortable,
            align: align,
            type: type,
            renderCell: ({ row }) => {
                const cell = row[key];
                const value = formatValue({
                    value: cell?.value,
                    isDate: cell?.isDate,
                    trueIcon,
                    falseIcon,
                });
                return cell?.onClick ?
                    _jsx(Link, { color: 'text.primary', onClick: cell.onClick, sx: { cursor: 'pointer' }, children: value }) :
                    value;
            },
            valueGetter: valueGetter ?? function ({ row }) {
                const cell = row[key];
                if (this.type === 'date') {
                    if (!cell?.value)
                        return undefined; // makes for effective date-sorting
                    return new Date(cell?.value);
                }
                return cell?.value;
            },
            ...rest
        };
    });
    const filteredRows = searchText ? rows.filter((row) => {
        return Object.keys(row).some((key) => {
            if (['rowProps', 'hasError'].includes(key))
                return false;
            const cell = row[key];
            return cell?.value?.toString().toLowerCase().includes(searchText.toLowerCase());
        });
    }) : rows;
    const keyAsId = headers.find(header => header.isId)?.key;
    /**
    * We need a unique row id for each row in the table.
    * A custom id comes in handy for testing purposes.
    * Otherwise we use the index of the row in the filteredRows array.
    */
    const getRowId = (row) => {
        let id;
        if (keyAsId) {
            const value = row[keyAsId]?.value;
            id = kebabCase(value.toString());
        }
        else {
            id = row.id ?? filteredRows.indexOf(row);
        }
        return `Row:${id}`;
    };
    const stickyParams = isStickyHeader ? {
        elevation: 1,
        [`.${gridClasses.main}`]: {
            overflow: 'unset'
        },
        [`.${gridClasses.columnHeaders}`]: {
            position: 'sticky',
            top: stickyColumnTop,
            zIndex: theme.zIndex.appBar,
            backgroundColor: theme.palette.background.paper,
        },
    } : null;
    /**
     * We calculate the height of the row based on the content of the cells to avoid the jumpy behavior.
     * We use 52px as the base height because it's the minimum value to provide space to the content;
     * and, increments of 20px because that is the average value per line break.
     */
    const calculateRowHeight = (params) => {
        if (rowHeight)
            return rowHeight;
        const row = params.model; // params.model contains the row data
        const baseHeight = 52;
        let additionalHeight = 0;
        Object.entries(row).forEach(([keys, value]) => {
            if (value && typeof value === 'string') {
                const lineBreaks = (value.match(/\n/g) || []).length;
                additionalHeight += lineBreaks * 20;
            }
        });
        return baseHeight + additionalHeight;
    };
    return _jsx(StyledDataGrid, { initialState: {
            sorting: sortBy ? {
                sortModel: [{ field: sortBy, sort: sortOrder }],
            } : undefined,
        }, 
        // TODO: If we add pagination in the future, we will need to modify this parameter value
        paginationMode: 'server', sortModel: (sortingMode === 'server' && sortBy) ? [{ field: sortBy, sort: sortOrder }] : undefined, columns: dataColumns, rows: filteredRows, getRowId: getRowId, hideFooter: true, onSortModelChange: onSortChange, disableColumnMenu: true, disableRowSelectionOnClick: onRowClick ? false : true, onRowClick: onRowClick ? params => onRowClick(params.row) : undefined, sortingMode: sortingMode, getRowClassName: params => params.row.hasError ? 'MuiDataGrid-row--error' : '', slots: {
            noRowsOverlay: () => _jsx(_Fragment, {}),
        }, sx: { borderWidth: 0, width: '100%', minHeight: '30px',
            '.MuiDataGrid-cell': {
                whiteSpace: 'normal !important',
                wordBreak: 'break-word',
            },
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
            '& .MuiDataGrid-virtualScroller': {
                overflowY: 'hidden', // Vertical scrollbar is hidden because this Grid height is undefined
                overflowX: 'auto',
            },
            '.MuiDataGrid-row--error': {
                backgroundColor: theme.palette.alert.errorFill,
                color: theme.palette.alert.errorContent,
            },
            ...stickyParams,
        }, getRowHeight: calculateRowHeight, sortingOrder: sortingOrder });
};
export default Table;
//# sourceMappingURL=index.js.map